/**
 * RenalUrineCard — iter1049 (Nephro1)
 * Урината като екран, мехурът и бъбречната безопасност на лекарствата.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Loader2, AlertTriangle, Droplets, HeartPulse } from "lucide-react";
import SickDayPanel from "./SickDayPanel";

const META = {
  suspect: { label: "Слабо място", cls: "bg-rose-500 text-white" },
  watch: { label: "Следи се", cls: "bg-amber-500 text-white" },
  ok: { label: "Работи", cls: "bg-emerald-500 text-white" },
  no_data: { label: "Няма данни", cls: "bg-slate-400 text-white" },
};

const DIARY_FIELDS = [
  ["urine_volume_ml", "Обем урина (ml/24ч)"],
  ["frequency_day", "Ходения през деня"],
  ["nocturia_count", "Ставания нощем"],
  ["urine_ph", "pH на урината"],
  ["specific_gravity", "Специфично тегло"],
];
const U24_FIELDS = [
  ["u24_calcium_mg", "Калций (mg/24ч)"],
  ["u24_oxalate_mg", "Оксалат (mg/24ч)"],
  ["u24_citrate_mg", "Цитрат (mg/24ч)"],
  ["u24_uric_acid_mg", "Урати (mg/24ч)"],
  ["u24_sodium_mmol", "Натрий (mmol/24ч)"],
];
const STONE_OPTIONS = [
  ["", "Не е известен"],
  ["ca_oxalate", "Калциево-оксалатен"],
  ["ca_phosphate", "Калциево-фосфатен"],
  ["uric_acid", "Уратен"],
  ["cystine", "Цистинов"],
  ["struvite", "Струвитен (инфекциозен)"],
];
const STRIP_FIELDS = [
  ["urine_protein", "Протеин"],
  ["urine_glucose", "Глюкоза"],
  ["urine_ketones", "Кетони"],
  ["urine_blood", "Кръв"],
];

export const RenalUrineCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [sickOpen, setSickOpen] = useState(false);
  const [open, setOpen] = useState(null);
  const [sel, setSel] = useState([]);
  const [diary, setDiary] = useState({});

  const apply = (d) => {
    setData(d);
    setSel(d.screen_selected || []);
    setDiary(d.diary || {});
  };

  useEffect(() => {
    axios.get(`${API}/renal/urine`, { timeout: 25000 })
      .then(({ data: d }) => apply(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(diary).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const { data: d } = await axios.post(`${API}/renal/screen`, { items: sel, diary: clean });
      apply(d);
      setOpenForm(false);
    } finally { setSaving(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  if (loading) {
    return <div className={`p-4 rounded-2xl border ${card}`} data-testid="renal-urine-loading">
      <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
    </div>;
  }
  if (!data) return null;

  return (
    <div className={`p-4 rounded-2xl border ${card}`} data-testid="renal-urine-card">
      <div className="flex items-center gap-2 mb-1">
        <Droplets className="w-4 h-4 text-cyan-500" />
        <h2 className="font-bold text-sm">Урина, мехур и бъбречна безопасност</h2>
      </div>
      <p className="text-[11px] opacity-70 mb-3" data-testid="renal-summary">{data.summary_bg}</p>

      <button onClick={() => setSickOpen((v) => !v)} data-testid="renal-sick-day-btn"
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-colors ${
                data.sick_day_active
                  ? "bg-amber-500 text-white hover:bg-amber-600"
                  : "bg-rose-600 text-white hover:bg-rose-700"
              }`}>
        <HeartPulse className="w-4 h-4" />
        {data.sick_day_active ? "Активен болен ден — отвори плана" : "Днес съм болен"}
      </button>
      {sickOpen && (
        <SickDayPanel isDark={isDark} onChanged={() => {
          axios.get(`${API}/renal/urine`, { timeout: 25000 })
            .then(({ data: d }) => apply(d)).catch(() => {});
        }} />
      )}
      <div className="mb-3" />

      {(data.red_flags || []).map((f) => (
        <div key={f.key} data-testid={`renal-flag-${f.key}`}
             className={`p-3 rounded-xl mb-2 border-2 ${
               f.urgency === "emergency"
                 ? (isDark ? "bg-rose-900/40 border-rose-500" : "bg-rose-50 border-rose-400")
                 : (isDark ? "bg-amber-900/30 border-amber-500/60" : "bg-amber-50 border-amber-300")
             }`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 ${f.urgency === "emergency" ? "text-rose-500" : "text-amber-500"}`} />
            <div className="min-w-0">
              <div className="text-xs font-bold">{f.title_bg}</div>
              <p className="text-[11px] mt-1 leading-relaxed opacity-90">{f.text_bg}</p>
              <div className="text-[11px] mt-1.5 font-semibold">{f.action_bg}</div>
            </div>
          </div>
        </div>
      ))}

      <div className="space-y-2">
        {(data.blocks || []).map((b) => {
          const m = META[b.status] || META.no_data;
          const isOpen = open === b.key;
          return (
            <div key={b.key} data-testid={`renal-block-${b.key}`}
                 className={`rounded-xl border ${isDark ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}>
              <button onClick={() => setOpen(isOpen ? null : b.key)}
                      data-testid={`renal-toggle-${b.key}`}
                      className="w-full text-left p-3 flex items-center gap-2">
                <span className="text-[11px] font-bold flex-1 min-w-0">{b.title_bg}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 whitespace-nowrap ${m.cls}`}
                      data-testid={`renal-status-${b.key}`}>{m.label}</span>
                <span className="text-[10px] opacity-50">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="text-[11px] font-semibold">{b.label_bg}</div>
                  <p className="text-[11px] leading-relaxed opacity-90">{b.explain_bg}</p>
                  <p className="text-[10px] italic opacity-75">{b.mechanism_bg}</p>
                  {(b.signals || []).length > 0 && (
                    <ul className="space-y-0.5" data-testid={`renal-signals-${b.key}`}>
                      {b.signals.map((s) => <li key={s} className="text-[10px] opacity-80">• {s}</li>)}
                    </ul>
                  )}
                  {b.stone_profile && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-cyan-500/10" : "bg-cyan-50"}`}
                         data-testid="renal-stone-profile">
                      <div className="font-bold">
                        {b.stone_profile.label_bg} — {b.stone_profile.confidence}
                      </div>
                      <p className="opacity-85 mt-0.5">{b.stone_profile.note_bg}</p>
                      {(b.stone_profile.drivers || []).map((d) => (
                        <div key={d} className="opacity-75">• {d}</div>
                      ))}
                    </div>
                  )}
                  {(b.levers || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-emerald-500/10" : "bg-emerald-50 border border-emerald-200"}`}
                         data-testid={`renal-levers-${b.key}`}>
                      <div className="font-bold opacity-80 mb-1">Лостове с числа</div>
                      {b.levers.map((l) => (
                        <div key={l.title_bg} className="mb-1.5">
                          <strong>{l.title_bg}</strong> — <span className="font-semibold">{l.target_bg}</span>
                          <div className="opacity-80">{l.why_bg}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(b.sick_day_rules || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${
                      b.sick_day_active
                        ? (isDark ? "bg-amber-900/30 border border-amber-500/50" : "bg-amber-50 border border-amber-300")
                        : (isDark ? "bg-slate-900/60" : "bg-white border border-slate-200")
                    }`} data-testid="renal-sick-day-rules">
                      <div className="font-bold opacity-80 mb-1">
                        Правила за болни дни{b.sick_day_active ? " — важи за днес" : ""}
                      </div>
                      {b.sick_day_rules.map((r) => <div key={r} className="mb-0.5">• {r}</div>)}
                    </div>
                  )}
                  {(b.missing || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                         data-testid={`renal-missing-${b.key}`}>
                      <div className="font-bold opacity-70 mb-1">Какво липсва, за да се реши</div>
                      {b.missing.map((x) => (
                        <div key={x.marker} className="mb-0.5"><strong>{x.label_bg}</strong> — {x.why_bg}</div>
                      ))}
                    </div>
                  )}
                  {(b.links || []).map((l, i) => (
                    <button key={`${b.key}-${i}`} onClick={() => navigate(l.route)}
                            data-testid={`renal-link-${b.key}-${i}`}
                            className={`w-full text-left p-2 rounded-lg text-[10px] transition-colors ${
                              l.urgency === "high"
                                ? (isDark ? "bg-rose-500/15 hover:bg-rose-500/25" : "bg-rose-50 hover:bg-rose-100")
                                : (isDark ? "bg-cyan-500/10 hover:bg-cyan-500/20" : "bg-cyan-50 hover:bg-cyan-100")
                            }`}>
                      <span className="font-bold">{l.title_bg} →</span>
                      <span className="opacity-80"> {l.action_bg}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={() => setOpenForm((v) => !v)} data-testid="renal-form-toggle"
              className={`mt-3 w-full text-[11px] font-semibold py-2 rounded-lg transition-colors ${
                isDark ? "bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25"
                       : "bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
              }`}>
        {openForm ? "Скрий въпросите" : `Дневник и кратка проверка (${(data.screen_selected || []).length} отбелязани)`}
      </button>

      {openForm && (
        <div className="mt-2 space-y-2" data-testid="renal-form">
          <div className="grid grid-cols-2 gap-2">
            {DIARY_FIELDS.map(([k, lbl]) => (
              <label key={k} className="text-[10px] opacity-80">
                {lbl}
                <input type="number" step="any" value={diary[k] ?? ""}
                       onChange={(e) => setDiary((d) => ({ ...d, [k]: e.target.value }))}
                       data-testid={`renal-input-${k}`}
                       className={`w-full mt-0.5 px-2 py-1 rounded-lg text-[11px] ${
                         isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                       }`} />
              </label>
            ))}
          </div>
          <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-slate-50 border border-slate-200"}`}>
            <div className="text-[10px] font-bold opacity-70 mb-1.5">
              24-часова урина (за профила на камъка)
            </div>
            <div className="grid grid-cols-2 gap-2">
              {U24_FIELDS.map(([k, lbl]) => (
                <label key={k} className="text-[10px] opacity-80">
                  {lbl}
                  <input type="number" step="any" value={diary[k] ?? ""}
                         onChange={(e) => setDiary((d) => ({ ...d, [k]: e.target.value }))}
                         data-testid={`renal-input-${k}`}
                         className={`w-full mt-0.5 px-2 py-1 rounded-lg text-[11px] ${
                           isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                         }`} />
                </label>
              ))}
            </div>
            <label className="text-[10px] opacity-80 block mt-2">
              Известен тип камък (от анализ)
              <select value={diary.stone_type ?? ""}
                      onChange={(e) => setDiary((d) => ({ ...d, stone_type: e.target.value }))}
                      data-testid="renal-input-stone_type"
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg text-[11px] ${
                        isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                      }`}>
                {STONE_OPTIONS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {STRIP_FIELDS.map(([k, lbl]) => (
              <label key={k} data-testid={`renal-strip-${k}`}
                     className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] cursor-pointer ${
                       diary[k] ? (isDark ? "bg-cyan-500/20" : "bg-cyan-50") : (isDark ? "bg-slate-800/40" : "bg-slate-50")
                     }`}>
                <input type="checkbox" checked={!!diary[k]}
                       onChange={() => setDiary((d) => ({ ...d, [k]: !d[k] }))} />
                Лентичка: {lbl}
              </label>
            ))}
          </div>
          <div className="space-y-1.5">
            {(data.screen_items || []).map((it) => (
              <label key={it.key} data-testid={`renal-screen-${it.key}`}
                     className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-[11px] ${
                       sel.includes(it.key) ? (isDark ? "bg-cyan-500/20" : "bg-cyan-50")
                                            : (isDark ? "bg-slate-800/40" : "bg-slate-50")
                     }`}>
                <input type="checkbox" checked={sel.includes(it.key)}
                       onChange={() => toggle(it.key)} className="mt-0.5" />
                <span>
                  <span className="opacity-50 text-[9px] uppercase font-bold block">{it.group_bg}</span>
                  {it.label_bg}
                </span>
              </label>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="renal-save"
                  className="w-full py-2 rounded-lg bg-cyan-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Записвам…" : "Запиши"}
          </button>
        </div>
      )}

      {(data.medications || []).length > 0 && (
        <div className={`mt-3 p-2.5 rounded-lg text-[10px] ${isDark ? "bg-slate-800/50" : "bg-slate-50 border border-slate-200"}`}
             data-testid="renal-meds">
          <div className="font-bold opacity-70 mb-1">Лекарства от Вашия списък, които влияят на бъбрека</div>
          {data.medications.map((m) => (
            <div key={m.name} className="mb-1"><strong>{m.name}</strong> ({m.group_bg}) — {m.why_bg}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RenalUrineCard;
